import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Utility function to conditionally join CSS class names together.
 * Combines clsx for conditional class logic and tailwind-merge for Tailwind CSS class deduplication.
 * 
 * @param inputs - Variable number of class values (strings, arrays, objects, or falsy values)
 * @returns A string of merged and deduplicated CSS class names
 * 
 * @example
 * ```tsx
 * // Basic usage
 * cn('text-red-500', 'text-blue-500') // Returns: 'text-blue-500'
 * 
 * // Conditional classes
 * cn('base-class', isActive && 'active-class') // Returns: 'base-class active-class' if isActive is true
 * 
 * // Arrays and objects
 * cn(['class1', 'class2'], { 'active': true, 'inactive': false }) // Returns: 'class1 class2 active'
 * 
 * // Complex conditional logic
 * cn(
 *   'button',
 *   isPrimary && 'bg-blue-500',
 *   isDisabled && 'opacity-50',
 *   size === 'large' && 'px-6 py-3'
 * )
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
